"use client";

import { useState, useEffect } from 'react';
import { ExternalLink, X } from 'lucide-react';

/**
 * Detects in-app browsers (X/Twitter, Facebook, Instagram, TikTok) on iOS
 * and shows a friendly banner suggesting to open in Safari for Apple Pay.
 * 
 * Not scary, not an error — just a helpful suggestion.
 */
export function InAppBrowserBanner() {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [browserName, setBrowserName] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent || '';
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isSafari = /Safari/i.test(ua) && !/Chrome|CriOS|FXiOS/i.test(ua);
    const isIOSWebView = isIOS && !isSafari;

    if (/Twitter/i.test(ua) || /X\//i.test(ua)) {
      setBrowserName('X');
      setShow(true);
    } else if (/Instagram/i.test(ua)) {
      setBrowserName('انستقرام');
      setShow(true);
    } else if (/FBAN|FBAV/i.test(ua)) {
      setBrowserName('فيسبوك');
      setShow(true);
    } else if (/TikTok/i.test(ua)) {
      setBrowserName('تيك توك');
      setShow(true);
    } else if (isIOSWebView) {
      setBrowserName('التطبيق');
      setShow(true);
    }
  }, []);

  if (!show || dismissed) return null;

  const handleCopy = () => {
    const url = window.location.href;
    try {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      }).catch(() => {
        fallbackCopy(url);
      });
    } catch (err) {
      console.error('[InAppBrowserBanner]', err);
      fallbackCopy(url);
    }
  };

  const fallbackCopy = (url: string) => {
    const a = document.createElement('textarea');
    a.value = url;
    a.style.position = 'fixed';
    a.style.opacity = '0';
    document.body.appendChild(a);
    a.focus();
    a.select();
    document.execCommand('copy');
    document.body.removeChild(a);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999]" dir="rtl">
      <div className="bg-gradient-to-l from-amber-50 to-orange-50 border-b border-amber-200/60 px-4 py-3 shadow-sm">
        <div className="max-w-4xl mx-auto flex items-start gap-3">
          <div className="text-xl mt-0.5 shrink-0">🍎</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-amber-900 font-medium leading-relaxed">
              لدفع أسرع عبر <span className="font-bold">Apple Pay</span>، افتح الرابط في سفاري
            </p>
            <button
              onClick={handleCopy}
              className={`mt-1.5 inline-flex items-center gap-1.5 text-xs font-bold rounded-lg px-3 py-1.5 transition-all ${
                copied
                  ? 'bg-green-100 text-green-700 border border-green-200'
                  : 'bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200 active:scale-95'
              }`}
            >
              {copied ? (
                <>✅ تم النسخ! افتح سفاري والصق</>
              ) : (
                <><ExternalLink className="h-3 w-3" /> نسخ الرابط وافتح في سفاري</>
              )}
            </button>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="shrink-0 text-amber-400 hover:text-amber-600 rounded-full p-1 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
